/**
 * Preferences subsection for installed-PWA push notifications on the feed.
 *
 * Master switch requests permission + registers a browser push subscription;
 * category switches gate which feed events trigger a push. All state is
 * server-persisted via `useFeedWebPush`. Only meaningful inside an installed
 * PWA, so non-standalone browsers get an explanatory note instead.
 */
import {
  AlertTriangle,
  BellRing,
  CalendarClock,
  Inbox,
  PackagePlus,
  RefreshCw,
  Sparkles,
  Star,
} from "lucide-react";

import { useUser } from "@/auth/UserContext";
import { useFeedWebPush } from "@/hooks/useFeedWebPush";
import {
  PreferenceGroup,
  PreferenceReveal,
  PreferenceSwitch,
} from "./PreferenceControls";
import { preferenceMatches } from "./preferenceSearch";

export const FEED_PUSH_SEARCH_TEXT =
  "push notifications installed app pwa home screen rebooks first books new bookings newly opened urgent warning alerts custom events reminders favorite starred bookings watch transshipment documents";

interface FeedPushPreferencesSectionProps {
  query: string;
  sectionMatched: boolean;
}

export function FeedPushPreferencesSection({
  query,
  sectionMatched,
}: FeedPushPreferencesSectionProps) {
  const {
    supported,
    standalone,
    permission,
    prefs,
    loading,
    busy,
    error,
    setMasterEnabled,
    setCategory,
  } = useFeedWebPush();
  const { isClient } = useUser();

  const enabled = prefs?.enabled ?? false;
  const categoriesDisabled = !enabled || busy || loading;

  const showMaster =
    sectionMatched ||
    preferenceMatches(
      query,
      "Push notifications (installed app)",
      "Requires installing Twido to your home screen and allowing notifications.",
      FEED_PUSH_SEARCH_TEXT,
    );
  const showRebooks =
    !isClient &&
    (sectionMatched ||
      preferenceMatches(query, "Rebooks", "Rebook opportunities and confirmations."));
  const showFirstBooks =
    !isClient &&
    (sectionMatched ||
      preferenceMatches(query, "First books", "First-book pricing recommendations."));
  const showNewBookings =
    !isClient &&
    (sectionMatched ||
      preferenceMatches(query, "New bookings", "High-confidence new-booking emails."));
  const showBatch =
    sectionMatched ||
    preferenceMatches(
      query,
      "Newly opened bookings",
      "Focus booking imports that added new bookings.",
    );
  const showUrgent =
    sectionMatched ||
    preferenceMatches(
      query,
      "Urgent & warning alerts",
      "Any urgent or warning activity on your feed, regardless of the categories above.",
    );
  const showFavorites =
    sectionMatched ||
    preferenceMatches(
      query,
      "Favorite bookings",
      "Verbose watch updates and urgent alerts for bookings you star.",
    );
  const showCustomEvents =
    sectionMatched ||
    preferenceMatches(
      query,
      "Custom events",
      "Backoffice reminders and alerts assigned to you.",
    );
  const showGroup =
    showMaster ||
    showRebooks ||
    showFirstBooks ||
    showNewBookings ||
    showBatch ||
    showUrgent ||
    showCustomEvents ||
    showFavorites;

  // A short status line explaining the current push capability.
  let note: string | null = null;
  if (!supported) {
    note = "This browser does not support push notifications.";
  } else if (!standalone) {
    note = "Install Twido to your home screen to enable push notifications.";
  } else if (permission === "denied") {
    note =
      "Notifications are blocked for this app. Enable them in your device settings to receive push.";
  } else if (prefs && !prefs.push_supported) {
    note = "Push delivery is not configured on the server yet.";
  }

  return (
    <PreferenceReveal visible={showGroup}>
      <PreferenceGroup
        title="Push notifications"
        description="Get notified on this device when new feed activity you follow arrives — even when the app is closed."
        icon={BellRing}
      >
        <PreferenceReveal visible={showMaster}>
          <div className="space-y-2">
            <PreferenceSwitch
              id="pref-push-enabled"
              icon={BellRing}
              label="Push notifications (installed app)"
              description="Requires installing Twido to your home screen and allowing notifications."
              checked={enabled}
              onCheckedChange={(value) => void setMasterEnabled(value)}
              disabled={busy || loading || !supported}
            />
            {note && <p className="text-xs text-muted-foreground">{note}</p>}
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        </PreferenceReveal>
        <PreferenceReveal visible={showRebooks}>
          <PreferenceSwitch
            id="pref-push-rebooks"
            icon={RefreshCw}
            label="Rebooks"
            description="Rebook opportunities and confirmations."
            checked={prefs?.rebooks_enabled ?? false}
            onCheckedChange={(value) => void setCategory("rebooks_enabled", value)}
            disabled={categoriesDisabled}
          />
        </PreferenceReveal>
        <PreferenceReveal visible={showFirstBooks}>
          <PreferenceSwitch
            id="pref-push-first-books"
            icon={Sparkles}
            label="First books"
            description="First-book pricing recommendations."
            checked={prefs?.first_books_enabled ?? false}
            onCheckedChange={(value) => void setCategory("first_books_enabled", value)}
            disabled={categoriesDisabled}
          />
        </PreferenceReveal>
        <PreferenceReveal visible={showNewBookings}>
          <PreferenceSwitch
            id="pref-push-new-bookings"
            icon={PackagePlus}
            label="New bookings"
            description="High-confidence new-booking emails."
            checked={prefs?.new_bookings_enabled ?? false}
            onCheckedChange={(value) =>
              void setCategory("new_bookings_enabled", value)
            }
            disabled={categoriesDisabled}
          />
        </PreferenceReveal>
        <PreferenceReveal visible={showBatch}>
          <PreferenceSwitch
            id="pref-push-batch-ingested"
            icon={Inbox}
            label="Newly opened bookings"
            description="Focus booking imports that added new bookings."
            checked={prefs?.batch_ingested_enabled ?? false}
            onCheckedChange={(value) =>
              void setCategory("batch_ingested_enabled", value)
            }
            disabled={categoriesDisabled}
          />
        </PreferenceReveal>
        <PreferenceReveal visible={showUrgent}>
          <PreferenceSwitch
            id="pref-push-urgent-warning"
            icon={AlertTriangle}
            label="Urgent & warning alerts"
            description="Any urgent or warning activity on your feed, regardless of the categories above."
            checked={prefs?.urgent_warning_enabled ?? false}
            onCheckedChange={(value) =>
              void setCategory("urgent_warning_enabled", value)
            }
            disabled={categoriesDisabled}
          />
        </PreferenceReveal>
        <PreferenceReveal visible={showCustomEvents}>
          <PreferenceSwitch
            id="pref-push-custom-events"
            icon={CalendarClock}
            label="Custom events"
            description="Backoffice reminders and alerts assigned to you. On by default when push is enabled."
            checked={prefs?.custom_events_enabled ?? true}
            onCheckedChange={(value) =>
              void setCategory("custom_events_enabled", value)
            }
            disabled={categoriesDisabled}
          />
        </PreferenceReveal>
        <PreferenceReveal visible={showFavorites}>
          <PreferenceSwitch
            id="pref-push-favorites-watch"
            icon={Star}
            label="Favorite bookings"
            description="Verbose watch updates (transshipment, documents) and urgent alerts for bookings you star — even outside your feed."
            checked={prefs?.favorites_watch_enabled ?? false}
            onCheckedChange={(value) =>
              void setCategory("favorites_watch_enabled", value)
            }
            disabled={categoriesDisabled}
          />
        </PreferenceReveal>
      </PreferenceGroup>
    </PreferenceReveal>
  );
}
